import React, { useState, useEffect } from 'react';
import { Monitor, RotateCcw } from 'lucide-react';
import { MapleLeaf } from '@/components/common/MapleLeaf';

interface SplashScreenProps {
  isVisible: boolean;
  onComplete: () => void;
}

export const SplashScreen: React.FC<SplashScreenProps> = ({ isVisible, onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [currentText, setCurrentText] = useState('Initializing MapleOS Reset...');
  const [backgroundOpacity, setBackgroundOpacity] = useState(0);

  const progressTexts = [
    'Initializing MapleOS Reset...',
    'Clearing virtual filesystem...',
    'Restoring default directories...',
    'Rebuilding system structure...',
    'Updating file registry...',
    'Syncing desktop icons...',
    'Finalizing reset process...',
    'MapleOS Reset Complete!'
  ];

  useEffect(() => {
    if (!isVisible) return;

    console.log('SplashScreen useEffect triggered, starting animation');
    
    let progressInterval: NodeJS.Timeout;
    let textIndex = 0;
    const minDisplayTime = 8000; // Minimum 8 seconds display time
    const startTime = Date.now();

    // Start progress immediately
    console.log('Starting progress animation immediately');
    progressInterval = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + 1.25; // Slower progress to ensure 8+ seconds
        
        // Gradually increase background opacity as progress increases (start at 0.3, go to 1.0)
        setBackgroundOpacity(0.3 + (newProgress / 100) * 0.7);
        
        // Update text based on progress
        const textProgressStep = 100 / progressTexts.length;
        const newTextIndex = Math.floor(newProgress / textProgressStep);
        if (newTextIndex !== textIndex && newTextIndex < progressTexts.length) {
          textIndex = newTextIndex;
          setCurrentText(progressTexts[textIndex]);
          console.log('Progress:', newProgress.toFixed(1) + '%', '-', progressTexts[textIndex]);
        }

        if (newProgress >= 100) {
          clearInterval(progressInterval);
          console.log('Progress complete, waiting for minimum display time');
          
          // Ensure minimum display time has passed
          const elapsedTime = Date.now() - startTime;
          const remainingTime = Math.max(0, minDisplayTime - elapsedTime);
          
          console.log('Elapsed time:', elapsedTime, 'Remaining time:', remainingTime);
          
          setTimeout(() => {
            console.log('Calling onComplete after minimum display time');
            onComplete();
          }, remainingTime + 1000); // Add 1 second for "Complete!" message
          return 100;
        }
        return newProgress;
      });
    }, 80); // 80ms intervals for smooth animation

    return () => {
      console.log('Cleaning up progress interval');
      clearInterval(progressInterval);
    };
  }, [isVisible, onComplete]);

  // Reset state when becoming visible
  useEffect(() => {
    if (isVisible) {
      console.log('Resetting splash state for new display');
      setProgress(0);
      setCurrentText(progressTexts[0]);
      setBackgroundOpacity(0.3); // Start semi-transparent so you can see the desktop behind
    }
  }, [isVisible]);

  if (!isVisible) return null;

  console.log('SplashScreen rendering, isVisible =', isVisible, 'progress =', progress);

  return (
    <div className="fixed inset-0 z-[10000] transition-all duration-1000 ease-out">
      {/* Fading background overlay */}
      <div 
        className="absolute inset-0 bg-gradient-to-br from-primary via-primary/90 to-primary/80 transition-opacity duration-1000 ease-out"
        style={{ opacity: backgroundOpacity }}
      />
      
      {/* Animated background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_25%,rgba(255,255,255,0.2)_0%,transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_75%,rgba(255,255,255,0.1)_0%,transparent_50%)]" />
      </div>

      {/* Main content */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="relative z-10 text-center text-primary-foreground w-full max-w-lg mx-auto">
        {/* Logo and title */}
        <div className="mb-12 animate-fade-in">
          <div className="mb-6 flex justify-center">
            <div className="relative">
              <MapleLeaf size={80} className="text-primary-foreground drop-shadow-lg" />
              <div className="absolute -top-2 -right-2 animate-spin">
                <RotateCcw size={24} className="text-accent" />
              </div>
            </div>
          </div>
          <h1 className="text-4xl font-bold mb-2 drop-shadow-lg">MapleOS</h1>
          <p className="text-lg opacity-90">System Reset in Progress</p>
        </div>

        {/* Loading spinner */}
        <div className="mb-8 flex justify-center">
          <div className="relative w-16 h-16">
            {/* Outer ring */}
            <div className="absolute inset-0 border-4 border-primary-foreground/20 rounded-full"></div>
            {/* Spinning progress ring */}
            <div 
              className="absolute inset-0 border-4 border-transparent border-t-primary-foreground rounded-full animate-spin"
              style={{
                borderRightColor: progress > 25 ? 'currentColor' : 'transparent',
                borderBottomColor: progress > 50 ? 'currentColor' : 'transparent',
                borderLeftColor: progress > 75 ? 'currentColor' : 'transparent',
              }}
            ></div>
            {/* Center dot */}
            <div className="absolute inset-4 bg-primary-foreground rounded-full animate-pulse"></div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mb-6">
          <div className="w-full bg-primary-foreground/20 rounded-full h-3 overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-primary-foreground to-accent rounded-full transition-all duration-300 ease-out relative"
              style={{ width: `${progress}%` }}
            >
              {/* Shimmer effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse"></div>
            </div>
          </div>
          <div className="mt-2 text-sm opacity-75">{progress}% Complete</div>
        </div>

        {/* Status text */}
        <div className="min-h-[1.5rem]">
          <p className="text-sm opacity-90 animate-pulse">{currentText}</p>
        </div>

        {/* Floating particles effect */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-primary-foreground/30 rounded-full animate-bounce"
              style={{
                left: `${20 + i * 12}%`,
                top: `${30 + (i % 2) * 40}%`,
                animationDelay: `${i * 0.2}s`,
                animationDuration: '2s'
              }}
            />
          ))}
        </div>
        </div>
      </div>

      {/* Bottom branding */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-center text-primary-foreground/70">
        <p className="text-xs">Please wait while we restore your system...</p>
      </div>
    </div>
  );
};